import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSEO } from "@/hooks/useSEO";

const milestones = [
  { year: "2020", title: "Company Founded", desc: "SE Distribution Sdn Bhd was incorporated in Malaysia with a clear mission: to bring world-class enterprise technology to Malaysian businesses." },
  { year: "2021", title: "First Authorized Partnerships", desc: "Secured authorized distributor agreements with Hytera and ExaGrid — establishing our foundation in body-worn cameras and enterprise backup." },
  { year: "2022", title: "+2 Brands: Arcfra & Synology Added", desc: "Expanded our ecosystem with Arcfra (HCI & hyperconverged infrastructure) and Synology (NAS & cloud storage) as technology partners — bringing our total to 4 active brand relationships."},
  { year: "2023", title: "+2 Brands: Honeywell & Dell Rugged Pro", desc: "Launched our Rugged Computing division with Honeywell and Dell Rugged Pro as technology partners — bringing the total ecosystem to 6 active brand relationships across security, storage, and field computing." },
  { year: "2024", title: "500+ Enterprise Clients", desc: "Crossed the milestone of 500 enterprise clients served across Malaysia, maintaining a 98% client retention rate." },
  { year: "2025", title: "AI Infrastructure Focus", desc: "Expanded Arcfra's positioning as a VMware and Nutanix alternative, delivering hyperconverged and AI-ready infrastructure to Malaysian enterprises." },
];

const values = [
  { icon: "ri-award-line", title: "Authorized & Genuine", desc: "Every product we distribute is 100% genuine, backed by full manufacturer warranty and authorized support channels." },
  { icon: "ri-lightbulb-line", title: "Solutions-First Thinking", desc: "We don't just sell products — we architect integrated solutions tailored to your specific operational challenges." },
  { icon: "ri-shake-hands-line", title: "Long-Term Partnership", desc: "Our 98% client retention rate reflects our commitment to being a trusted technology partner, not just a vendor." },
  { icon: "ri-map-pin-2-line", title: "Local Expertise", desc: "Based in Kuala Lumpur, we understand the Malaysian market, regulatory landscape, and enterprise procurement requirements." },
];

const distributorCredentials = [
  { brand: "Hytera", type: "Authorized Distributor", region: "Malaysia", logo: "https://storage.readdy-site.link/project_files/15616acb-dd20-42a3-bb13-24e14d98a32e/6d2ef031-b05d-445b-add7-cb1ee7ec1599_Hytera-Logo-RGB.png?v=2b451d70b2f39a7a3313fb4cfd9d1d6a", invert: false },
  { brand: "ExaGrid", type: "Authorized Distributor", region: "Malaysia", logo: "https://storage.readdy-site.link/project_files/15616acb-dd20-42a3-bb13-24e14d98a32e/332e9681-9df1-4dcd-93f2-cf1098c6260f_exagrid-logo.png?v=ddd934934df6f2e00eabba5539238300", invert: false },
  { brand: "Arcfra", type: "Authorized Distributor", region: "Malaysia", logo: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/e4390374f33642fbb291c106f3ac5eaa.jpeg", invert: false },
];

const techPartnerCredentials = [
  { brand: "Synology", type: "Technology Partner", region: "Malaysia", logo: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/f3fe09ee56f23a05de110d3c308e3d60.png", invert: false },
  { brand: "Honeywell", type: "Technology Partner", region: "Malaysia", logo: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/6cb0550feabd77beb61e8450691b1813.png", invert: false },
  { brand: "Dell Rugged Pro", type: "Technology Partner", region: "Malaysia", logo: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/b7a6eed8a3607d563d28271b61809705.png", invert: false },
];

export default function AboutPage() {
  useSEO({
    title: "About SE Distribution Malaysia | Authorized Hytera, Arcfra & ExaGrid Distributor | Kuala Lumpur",
    description: "SE Distribution Sdn Bhd — Malaysia's authorized distributor for Hytera, Arcfra & ExaGrid. Founded 2020, 500+ enterprise clients, 98% retention. Based in Mont Kiara, Kuala Lumpur.",
    canonical: "https://www.sedistribution.com.my/about",
    keywords: "SE Distribution Malaysia, authorized distributor Malaysia, Hytera distributor Malaysia, Arcfra distributor Malaysia, ExaGrid distributor Malaysia, enterprise IT Malaysia, Kuala Lumpur",
  });
  const [formData, setFormData] = useState({ name: "", email: "", company: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const data = new URLSearchParams();
    Object.entries(formData).forEach(([k, v]) => data.append(k, v));
    try {
      await fetch("https://readdy.ai/api/form/d7j1qb83a2kq2q6kqn3g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
    } catch {
      // silent
    }
    setSubmitting(false);
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20">

        {/* Hero */}
        <section className="relative bg-[#1a1a1a] py-24 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20Kuala%20Lumpur%20city%20skyline%20at%20dusk%2C%20KLCC%20twin%20towers%2C%20warm%20golden%20hour%20light%2C%20aerial%20perspective%2C%20dramatic%20sky%2C%20professional%20architectural%20photography%2C%20cinematic%20wide%20shot&width=1400&height=600&seq=about-hero1&orientation=landscape"
            alt="SE Distribution About Us"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a]/70 to-[#1a1a1a]/90"></div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 text-center">
            <div className="flex items-center justify-center mb-6">
              <img
                src="https://storage.readdy-site.link/project_files/15616acb-dd20-42a3-bb13-24e14d98a32e/707ceb8d-a118-467e-a1f1-a92ec3214991_SED-Logo.png?v=25b700dde891a260d3a0501eba34a289"
                alt="SE Distribution"
                className="h-14 w-auto"
              />
            </div>
            <span className="text-xs font-semibold tracking-widest uppercase text-white/50 mb-4 block">About SE Distribution</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Advanced IT Solutions for <br /><strong>Modern Security</strong>
            </h1>
            <p className="text-white/65 text-base max-w-2xl mx-auto leading-relaxed mb-8">
              From body-worn camera systems and AI infrastructure to enterprise-grade backup enhancement — we deliver the technology your business needs to stay secure, resilient, and ahead.
            </p>

            {/* Ecosystem Badge Strip */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
              <div className="flex items-center gap-2.5 bg-white/10 border border-white/20 rounded-lg px-4 py-2.5">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-verified-badge-fill text-white text-sm"></i>
                </div>
                <div className="text-left">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/50 leading-none mb-0.5">3 Authorized Distributor</p>
                  <p className="text-xs font-semibold text-white leading-none">Hytera &middot; Arcfra &middot; ExaGrid</p>
                </div>
              </div>
              <div className="hidden sm:flex w-px h-8 bg-white/20"></div>
              <div className="flex items-center gap-2.5 bg-white/10 border border-white/20 rounded-lg px-4 py-2.5">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-shake-hands-fill text-white/70 text-sm"></i>
                </div>
                <div className="text-left">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/50 leading-none mb-0.5">3 Technology Partner</p>
                  <p className="text-xs font-semibold text-white leading-none">Synology &middot; Honeywell &middot; Dell Rugged Pro</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Story */}
        <section className="py-20 max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-14 items-center">
            <div className="w-full lg:w-1/2">
              <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">Our Mission</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-6 leading-tight">
                Bridging World-Class Technology with Malaysian Enterprise
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                SE Distribution Sdn Bhd was founded in 2020 with a singular purpose: to make enterprise-grade technology accessible, deployable, and supportable for Malaysian organizations — from government agencies and law enforcement to private enterprises and SMBs.
              </p>
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                We are the <strong>authorized distributor</strong> for Hytera, Arcfra, and ExaGrid — and a <strong>technology partner</strong> for Synology, Honeywell, and Dell Rugged Pro — spanning body-worn cameras, rugged computing, AI-ready storage infrastructure, and enterprise backup systems. Every solution we deliver is backed by genuine manufacturer support, full warranty coverage, and our own local technical team.
              </p>
              <p className="text-gray-500 text-sm leading-relaxed">
                Our approach is solutions-first: we don't just sell products. We assess your operational challenges, design integrated systems, and provide ongoing support to ensure measurable outcomes — whether that's faster evidence management, zero-downtime data recovery, or field teams that stay connected in any environment.
              </p>
            </div>
            <div className="w-full lg:w-1/2 h-80 rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20professional%20office%20meeting%20room%20technology%20company%20Malaysia%2C%20team%20collaboration%2C%20clean%20minimalist%20interior%2C%20warm%20lighting%2C%20glass%20walls%2C%20professional%20corporate%20environment%2C%20wide%20angle%20photography&width=700&height=450&seq=about-office1&orientation=landscape"
                alt="SE Distribution Office"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-[#1a1a1a] py-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
              {[
                { value: "2020", label: "Year Founded" },
                { value: "500+", label: "Enterprise Clients" },
                { value: "3+3", label: "Distributor & Tech Partners" },
                { value: "98%", label: "Client Retention Rate" },
              ].map((s) => (
                <div key={s.label}>
                  <p className="text-3xl md:text-4xl font-bold text-white mb-1">{s.value}</p>
                  <p className="text-white/50 text-xs uppercase tracking-widest">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-20 bg-[#f9f7f4]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">What We Stand For</span>
              <h2 className="text-3xl font-bold text-[#1a1a1a]">Our Core Values</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((v) => (
                <div key={v.title} className="bg-white rounded-lg p-6 border border-gray-100">
                  <div className="w-11 h-11 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg mb-4">
                    <i className={`${v.icon} text-[#1a1a1a] text-xl`}></i>
                  </div>
                  <h3 className="font-semibold text-[#1a1a1a] text-sm mb-2">{v.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Company Timeline */}
        <section className="py-20 max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">Our Journey</span>
            <h2 className="text-3xl font-bold text-[#1a1a1a]">Company Milestones</h2>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-200 hidden lg:block"></div>
            <div className="space-y-10">
              {milestones.map((m, i) => (
                <div key={m.year} className={`flex flex-col lg:flex-row gap-6 items-start lg:items-center ${i % 2 === 1 ? "lg:flex-row-reverse" : ""}`}>
                  <div className={`w-full lg:w-5/12 ${i % 2 === 1 ? "lg:text-right" : ""}`}>
                    <div className="bg-white border border-gray-100 rounded-lg p-6">
                      <span className="text-xs font-bold tracking-widest text-gray-400 uppercase mb-2 block">{m.year}</span>
                      <h3 className="font-bold text-[#1a1a1a] text-base mb-2">{m.title}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                  <div className="hidden lg:flex w-2/12 justify-center">
                    <div className="w-4 h-4 rounded-full bg-[#1a1a1a] border-4 border-white ring-2 ring-[#1a1a1a]/20"></div>
                  </div>
                  <div className="w-full lg:w-5/12"></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Partner Credentials */}
        <section className="py-20 bg-[#f9f7f4]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">Official Credentials</span>
              <h2 className="text-3xl font-bold text-[#1a1a1a] mb-3">Our Technology Partner Ecosystem</h2>
              <p className="text-gray-500 text-sm max-w-lg mx-auto">
                SE Distribution is the <strong className="text-[#1a1a1a]">authorized distributor</strong> for Hytera, Arcfra &amp; ExaGrid in Malaysia — and a <strong className="text-[#1a1a1a]">technology partner</strong> for 3 additional world-class brands.
              </p>
            </div>

            {/* Authorized Distributors */}
            <div className="mb-10">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-7 h-7 flex items-center justify-center bg-[#1a1a1a] rounded-md">
                  <i className="ri-verified-badge-line text-white text-sm"></i>
                </div>
                <h3 className="text-sm font-bold text-[#1a1a1a] uppercase tracking-wider">Authorized Distributor — Malaysia</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {distributorCredentials.map((c) => (
                  <div key={c.brand} className="bg-white border-2 border-[#1a1a1a]/10 rounded-lg p-5 flex flex-col items-center text-center">
                    <div className="w-full h-14 flex items-center justify-center mb-3">
                      <img
                        src={c.logo}
                        alt={c.brand}
                        className="max-h-12 max-w-full object-contain"
                        style={{ filter: c.invert ? "brightness(0) invert(1)" : "none" }}
                      />
                    </div>
                    <p className="text-[#1a1a1a] text-xs font-bold mb-1">{c.brand}</p>
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-[#1a1a1a] text-white px-2 py-0.5 rounded-full">{c.type}</span>
                    <span className="mt-2 text-xs text-gray-400">{c.region}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Technology Partners */}
            <div>
              <div className="flex items-center gap-2 mb-5">
                <div className="w-7 h-7 flex items-center justify-center bg-[#f5f3ee] border border-gray-200 rounded-md">
                  <i className="ri-shake-hands-line text-[#1a1a1a] text-sm"></i>
                </div>
                <h3 className="text-sm font-bold text-[#1a1a1a] uppercase tracking-wider">Technology Partners</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {techPartnerCredentials.map((c) => (
                  <div key={c.brand} className="bg-white border border-gray-100 rounded-lg p-5 flex flex-col items-center text-center">
                    <div className="w-full h-14 flex items-center justify-center mb-3">
                      <img
                        src={c.logo}
                        alt={c.brand}
                        className="max-h-12 max-w-full object-contain"
                        style={{ filter: c.invert ? "brightness(0) invert(1)" : "none" }}
                      />
                    </div>
                    <p className="text-[#1a1a1a] text-xs font-semibold mb-1">{c.brand}</p>
                    <span className="text-[10px] font-medium text-gray-500 bg-[#f5f3ee] px-2 py-0.5 rounded-full">{c.type}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-10 text-center">
              <div className="inline-flex items-center gap-3 bg-white border border-gray-200 rounded-lg px-6 py-4">
                <i className="ri-verified-badge-line text-[#1a1a1a] text-xl"></i>
                <div className="text-left">
                  <p className="text-[#1a1a1a] text-sm font-semibold">SE Distribution Sdn Bhd</p>
                  <p className="text-gray-400 text-xs">Registration No: 202001036939 (1393260-U) &bull; Kuala Lumpur, Malaysia</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Office Info + Contact Form */}
        <section className="py-20 bg-[#f9f7f4]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex flex-col lg:flex-row gap-12">
              {/* Office Info */}
              <div className="w-full lg:w-2/5">
                <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">Find Us</span>
                <h2 className="text-2xl font-bold text-[#1a1a1a] mb-6">Our Office</h2>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg flex-shrink-0">
                      <i className="ri-map-pin-line text-[#1a1a1a] text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#1a1a1a] mb-0.5">Registered Address</p>
                      <p className="text-sm text-gray-500 leading-relaxed">Unit 21-11, Menara 1MK Kompleks,<br />Mont Kiara, 50480 Kuala Lumpur,<br />Malaysia</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg flex-shrink-0">
                      <i className="ri-phone-line text-[#1a1a1a] text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#1a1a1a] mb-0.5">Office Phone</p>
                      <a href="tel:+60364127917" className="text-sm text-gray-500 hover:text-[#1a1a1a] transition-colors">+603 6412 7917</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg flex-shrink-0">
                      <i className="ri-mail-line text-[#1a1a1a] text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#1a1a1a] mb-0.5">Sales Enquiries</p>
                      <a href="mailto:sales@sedistribution.com.my" className="text-sm text-gray-500 hover:text-[#1a1a1a] transition-colors">sales@sedistribution.com.my</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg flex-shrink-0">
                      <i className="ri-time-line text-[#1a1a1a] text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#1a1a1a] mb-0.5">Business Hours</p>
                      <p className="text-sm text-gray-500">Monday – Friday<br />9:30 AM – 6:30 PM (GMT+8)</p>
                    </div>
                  </div>
                </div>
                <div className="mt-6">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Full Contact Page
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>

              {/* Quick Enquiry Form */}
              <div className="w-full lg:w-3/5 bg-white rounded-xl p-8 border border-gray-100">
                <h3 className="text-xl font-bold text-[#1a1a1a] mb-1">Send Us a Quick Message</h3>
                <p className="text-sm text-gray-400 mb-6">We&apos;ll get back to you within 1 business day.</p>
                {submitted ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-14 h-14 flex items-center justify-center bg-green-50 rounded-full mb-4">
                      <i className="ri-check-line text-green-600 text-2xl"></i>
                    </div>
                    <h4 className="font-bold text-[#1a1a1a] mb-2">Message Sent!</h4>
                    <p className="text-sm text-gray-500">Thank you for reaching out. Our team will be in touch shortly.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1.5">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Ahmad bin Abdullah"
                          required
                          className="w-full text-sm border border-gray-200 rounded-md px-3 py-2.5 outline-none focus:border-[#1a1a1a] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1.5">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="you@company.com.my"
                          required
                          className="w-full text-sm border border-gray-200 rounded-md px-3 py-2.5 outline-none focus:border-[#1a1a1a] transition-colors"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Company Name</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your Company Sdn Bhd"
                        className="w-full text-sm border border-gray-200 rounded-md px-3 py-2.5 outline-none focus:border-[#1a1a1a] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Message *</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your requirements..."
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full text-sm border border-gray-200 rounded-md px-3 py-2.5 outline-none focus:border-[#1a1a1a] transition-colors resize-none"
                      />
                      <p className="text-xs text-gray-400 mt-1 text-right">{formData.message.length}/500</p>
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-[#1a1a1a] text-white text-sm font-semibold py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                    >
                      {submitting ? "Sending..." : "Send Message"}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
